import React, { useMemo } from 'react'
import { User } from 'lucide-react'
import apiService from '../services/api'
import { useTranslation } from 'react-i18next'
import useAppStore from '../store/useAppStore'

const Welcome = () => {
  const { t } = useTranslation()
  const telegramSdkAvailable = useAppStore(state => state.telegramSdkAvailable)
  const tags = useMemo(() => {
    return [
      { label: t('welcome.tags.players', { count: '1 050 830' }), tone: 'green', icon: 'user' },
      { label: t('welcome.tags.rating'), tone: 'blue' },
      { label: t('welcome.tags.withdrawal'), tone: 'purple' },
    ]
  }, [t])

  const providers = useMemo(() => {
    return [
      {
        id: 'telegram',
        title: 'Telegram',
        icon: '/images/welcome-page/telegram.png',
        tone: 'telegram',
        primary: true,
        href: apiService.getAuthLoginUrl('telegram')
      },
      {
        id: 'steam',
        title: 'Steam',
        icon: '/images/welcome-page/steam.png',
        tone: 'steam',
        href: apiService.getAuthLoginUrl('steam')
      },
      {
        id: 'google',
        title: 'Google',
        icon: '/images/welcome-page/google.png',
        tone: 'google',
        href: apiService.getAuthLoginUrl('google')
      },
      {
        id: 'discord',
        title: 'Discord',
        icon: '/images/welcome-page/discord.png',
        tone: 'discord',
        href: apiService.getAuthLoginUrl('discord')
      }
    ]
  }, [])

  const visibleProviders = useMemo(() => {
    return providers.filter((provider) => {
      if (provider.id === 'telegram') {
        return telegramSdkAvailable
      }

      return true
    })
  }, [providers, telegramSdkAvailable])

  return (
    <div className="min-h-screen lg:h-screen bg-dark-primary text-white overflow-hidden">
      {/*
        Десктоп: 2 колонки 50/50 (слева фон-картинка, справа контент)
        Мобилка: картинка сверху, контент снизу
      */}
      <div className="min-h-screen lg:h-screen grid grid-rows-[260px_1fr] lg:grid-rows-1 lg:grid-cols-2">
        {/* Левая часть: фоновая картинка */}
        <div
          className="relative overflow-hidden"
          style={{
            backgroundImage: 'url(/images/welcome-page/welcome-background.jpg)',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        >
          {/* Плавный переход на стыке с правой секцией (только на десктопе) */}
          <div className="hidden lg:block absolute inset-y-0 right-0 w-64 bg-gradient-to-r from-transparent to-dark-primary pointer-events-none" />
        </div>

        {/* Правая часть: контент */}
        <div className="relative bg-dark-primary">
          {/* Фоновый слой справа (легкие акценты) */}
          <div className="absolute inset-0">
            <div className="absolute -top-40 -right-40 w-[520px] h-[520px] rounded-full bg-accent-primary/10 blur-3xl" />
            {/* <div className="absolute -bottom-56 -right-56 w-[640px] h-[640px] rounded-full bg-accent-secondary/10 blur-3xl" />
            <div className="absolute inset-0 bg-gradient-to-b from-black/0 via-black/20 to-black/60" /> */}
          </div>

          <div className="relative z-10 h-full px-6 py-10 flex">
            <div className="w-full max-w-[540px] mx-auto flex flex-col justify-center">
              <div className="text-center">
                <h1
                  data-text="SKINOZ"
                  className="glitch text-6xl sm:text-7xl font-orbitron font-black text-transparent bg-gradient-to-r from-accent-primary to-accent-secondary bg-clip-text"
                >
                  SKINOZ
                </h1>

                <p className="mt-6 text-3xl sm:text-5xl tracking-tight">
                  {t('welcome.heroTitle')}
                </p>
              </div>

              {/* Теги / метрики */}
              <div className="mt-16 flex flex-wrap justify-center gap-2">
                {tags.map((tag) => {
                  const toneClasses =
                    tag.tone === 'green'
                      ? 'bg-accent-primary/10 border-accent-primary/20 text-accent-primary'
                      : tag.tone === 'blue'
                        ? 'bg-info/10 border-info/20 text-info'
                        : tag.tone === 'purple'
                          ? 'bg-purple-400/10 border-purple-400/20 text-purple-300'
                          : 'bg-white/5 border-white/10 text-text-secondary'

                  return (
                    <div
                      key={tag.label}
                      className={`px-3 py-1.5 rounded-full border text-sm ${toneClasses}`}
                    >
                      {tag.icon === 'user' && <User size={14} className="inline mr-1.5 -mt-0.5" />}
                      {tag.label}
                    </div>
                  )
                })}
              </div>

              {/* Кнопки авторизации */}
              <div className="mt-16 w-full">
                <div className="grid grid-cols-1 gap-3">
                  {visibleProviders.filter((provider) => provider.primary).map((provider) => {
                    return (
                      <a
                        key={provider.id}
                        href={provider.href}
                        className="group block w-full px-5 py-4 rounded-2xl border border-cyan-400/40 bg-gradient-to-r from-cyan-400/20 to-accent-primary/20 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_0_30px_rgba(34,211,238,0.25)]"
                      >
                        <div className="flex items-center justify-center gap-3">
                          <img
                            src={provider.icon}
                            alt={provider.title}
                            className="w-11 h-11 object-contain shrink-0"
                            loading="lazy"
                          />
                          <div className="text-lg font-bold tracking-tight">
                            {t('welcome.loginWith', { provider: provider.title })}
                          </div>
                        </div>
                      </a>
                    )
                  })}
                </div>

                <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {visibleProviders.filter((provider) => !provider.primary).map((provider) => {
                    const accentClasses =
                      provider.tone === 'steam'
                        ? 'hover:border-sky-400/40 hover:bg-sky-400/10 hover:shadow-[0_0_24px_rgba(56,189,248,0.2)]'
                        : provider.tone === 'google'
                          ? 'hover:border-red-400/35 hover:bg-red-400/10 hover:shadow-[0_0_24px_rgba(248,113,113,0.18)]'
                          : provider.tone === 'discord'
                            ? 'hover:border-indigo-400/40 hover:bg-indigo-400/12 hover:shadow-[0_0_24px_rgba(129,140,248,0.2)]'
                            : 'hover:border-cyan-400/40 hover:bg-cyan-400/10 hover:shadow-[0_0_24px_rgba(34,211,238,0.18)]'

                    return (
                      <a
                        key={provider.id}
                        href={provider.href}
                        className={`group block w-full px-4 py-3 rounded-2xl border border-white/10 bg-gradient-to-br from-white/[0.09] to-white/[0.04] transition-all duration-300 hover:-translate-y-0.5 ${accentClasses}`}
                      >
                        <div className="flex items-center gap-3">
                          <img
                            src={provider.icon}
                            alt={provider.title}
                            className="w-9 h-9 object-contain shrink-0"
                            loading="lazy"
                          />
                          <div className="text-left">
                            <div className="text-sm sm:text-base font-semibold tracking-tight">
                              {provider.title}
                            </div>
                          </div>
                        </div>
                      </a>
                    )
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Welcome
